import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';

import { BugoRegionResult } from './adminBugoRegion.types';
import { sigunguLabelOf } from './adminBugoRegion.util';
import { rateOf } from './adminBugoStat.util';
import { autoWidth, freezeHeader } from './adminBugoStatExcel';
import { sidoLabelOf } from './koreaMap/koreaRegion';

//* 프로젝트 표준(커뮤니티 xlsx + file-saver) — 스타일 플러그인 없이 너비/고정/필터만

type Cell = string | number | null;

const round1 = (value: number | null): number | null =>
  value === null ? null : Math.round(value * 10) / 10;

const sheetOf = (wb: XLSX.WorkBook, name: string, rows: Cell[][], filterRows: number) => {
  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws['!cols'] = autoWidth(rows);
  freezeHeader(ws);
  if (filterRows > 0 && rows[0]) {
    ws['!autofilter'] = {
      ref: XLSX.utils.encode_range({
        s: { r: 0, c: 0 },
        e: { r: filterRows, c: rows[0].length - 1 },
      }),
    };
  }
  XLSX.utils.book_append_sheet(wb, ws, name);
};

export const exportBugoRegionExcel = (
  result: BugoRegionResult,
  period: { from: string; to: string },
): void => {
  const total = result.totals.feventCount;
  const wb = XLSX.utils.book_new();

  const sidoRows: Cell[][] = [
    ['시도', '상가수', '비중(%)', '공유 상가', '공유율(%)', '장례식장 수', '시군구 수'],
    ...result.sidos.map((row) => [
      row.label,
      row.feventCount,
      round1(rateOf(row.feventCount, total)),
      row.sharedFeventCount,
      round1(rateOf(row.sharedFeventCount, row.feventCount)),
      row.funeralHomeCount,
      row.sigunguCount,
    ]),
    [
      '합계',
      total,
      total > 0 ? 100 : null,
      result.totals.sharedFeventCount,
      round1(rateOf(result.totals.sharedFeventCount, total)),
      result.totals.funeralHomeCount,
      result.totals.sigunguCount,
    ],
  ];
  sheetOf(wb, '시도별', sidoRows, result.sidos.length);

  const sigunguRows: Cell[][] = [
    ['시도', '시군구', '상가수', '비중(%)', '공유 상가', '공유율(%)', '장례식장 수'],
    ...result.sigungus.map((row) => [
      sidoLabelOf(row.sido),
      sigunguLabelOf(row.sido, row.sigungu),
      row.feventCount,
      round1(rateOf(row.feventCount, total)),
      row.sharedFeventCount,
      round1(rateOf(row.sharedFeventCount, row.feventCount)),
      row.funeralHomeCount,
    ]),
  ];
  sheetOf(wb, '시군구별', sigunguRows, result.sigungus.length);

  const fhRows: Cell[][] = [
    [
      '순위',
      '장례식장',
      '시도',
      '시군구',
      '주소',
      '상가수',
      '비중(%)',
      '공유 상가',
      '공유율(%)',
      '상조 수',
      '주 상조',
      '주 상조 상가수',
    ],
    ...result.funeralHomes.map((row, index) => [
      index + 1,
      row.name,
      sidoLabelOf(row.sido),
      sigunguLabelOf(row.sido, row.sigungu),
      row.address,
      row.feventCount,
      round1(rateOf(row.feventCount, total)),
      row.sharedFeventCount,
      round1(rateOf(row.sharedFeventCount, row.feventCount)),
      row.brandCount,
      row.topBrandName,
      row.topBrandCount,
    ]),
  ];
  sheetOf(wb, '장례식장별', fhRows, result.funeralHomes.length);

  //* 월별 × 시도 — 열은 상가 수 순서의 시도, 미분류는 맨 뒤
  const sidoKeys = result.sidos.map((row) => row.key);
  const monthRows: Cell[][] = [
    ['발인월', '상가수', ...sidoKeys.map(sidoLabelOf)],
    ...result.months.map((month) => [
      month.ym,
      month.feventCount,
      ...sidoKeys.map((key) => month.bySido[key] ?? 0),
    ]),
  ];
  sheetOf(wb, '월별', monthRows, 0);

  const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(
    new Blob([out], { type: 'application/octet-stream' }),
    `부고지역통계_${period.from}_${period.to}.xlsx`,
  );
};
